import API_BASE from './config';
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Dashboard = ({ eventId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedSegmentId, setSelectedSegmentId] = useState('');

    useEffect(() => {
        const fetchAnalytics = async () => {
            const token = localStorage.getItem('access_token');
            try {
                // FIXED: Added Authorization header to the request
                const res = await axios.get(`${API_BASE}/api/events/${eventId}/analytics/`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setData(res.data);
                setLoading(false);
            } catch (err) {
                console.error("Dashboard Error:", err);
                setLoading(false);
            }
        };

        if (eventId) fetchAnalytics();
    }, [eventId]);

    // NEW: Scoped & Authenticated Export Handler
    const handleExport = async () => {
        const token = localStorage.getItem('access_token');
        try {
            // Fetching as a blob to handle binary data through axios with headers
            const exportUrl = selectedSegmentId
                ? `${API_BASE}/api/registrations/export_event_csv/?event_id=${eventId}&segment_id=${selectedSegmentId}`
                : `${API_BASE}/api/registrations/export_event_csv/?event_id=${eventId}`;

            const response = await axios.get(exportUrl, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob', // Important for file downloads
            });

            // Create a local URL for the downloaded file
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const selectedSegment = data?.segments?.find(seg => String(seg.id) === String(selectedSegmentId));
            const filename = selectedSegment
                ? `${selectedSegment.title.replace(/[^a-z0-9_\- ]/gi, '').replace(/\s+/g, '_')}_registrations.csv`
                : `event_${eventId}_registrations.csv`;

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error("Export Failed:", err);
            alert("ACCESS DENIED: UNABLE TO EXPORT DATA.");
        }
    };

    if (loading) return <div className="text-sky-500 font-black animate-pulse uppercase tracking-[0.3em]">Booting Command Center...</div>;
    if (!data) return <div className="text-red-500 font-black uppercase text-center p-10 border border-red-500/20 rounded-[2.5rem]">Failed to Load Intelligence.</div>;

    return (
        <div className="space-y-8 animate-in fade-in duration-700">
            <header className="flex justify-between items-end">
                <div>
                    <p className="text-sky-500 text-[10px] font-bold uppercase tracking-[0.5em] mb-2">Live Intelligence</p>
                    <h2 className="text-4xl font-black tracking-tighter uppercase text-white">{data.event_name}</h2>
                </div>
                <div className="text-right">
                    <p className="text-gray-500 text-[10px] font-bold uppercase tracking-widest">System Status</p>
                    <p className="text-green-500 text-xs font-bold uppercase tracking-widest">Operational</p>
                </div>
            </header>

            {/* KEY METRICS GRID */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white/[0.03] border border-white/[0.05] p-8 rounded-[2.5rem]">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">Total Revenue</p>
                    <p className="text-3xl font-black text-sky-500">{data.stats.revenue.toLocaleString()} <span className="text-xs">BDT</span></p>
                </div>
                <div className="bg-white/[0.03] border border-white/[0.05] p-8 rounded-[2.5rem]">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">Verified Reg</p>
                    <p className="text-3xl font-black text-white">{data.stats.verified} / {data.stats.total}</p>
                </div>
                <div className="bg-white/[0.03] border border-white/[0.05] p-8 rounded-[2.5rem]">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">Check-ins</p>
                    <p className="text-3xl font-black text-green-500">{data.stats.checked_in}</p>
                </div>
            </div>

            {/* SEGMENT PERFORMANCE */}
            <div className="bg-white/[0.02] border border-white/[0.05] rounded-[2.5rem] p-8">
                <h3 className="text-sm font-black uppercase tracking-widest mb-6 text-gray-400">Competition Segments</h3>
                <div className="space-y-4">
                    {data.segments.map((seg, i) => (
                        <div key={i} className="flex items-center justify-between p-4 bg-white/[0.02] rounded-2xl border border-white/5">
                            <span className="text-sm font-bold text-white uppercase">{seg.title}</span>
                            <span className="text-xs font-mono text-sky-500">{seg.count} Registration</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* DATA MANAGEMENT SECTION */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 p-8 bg-white/[0.02] border border-white/[0.05] rounded-[2.5rem]">
                <div>
                    <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-500">Data Management</h3>
                    <p className="text-[9px] text-gray-600 uppercase mt-1 italic">Authorized personnel only</p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <select
                        value={selectedSegmentId}
                        onChange={(e) => setSelectedSegmentId(e.target.value)}
                        className="bg-white/[0.03] border border-white/[0.08] text-white px-4 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest outline-none"
                    >
                        <option value="">All Segments</option>
                        {data.segments.map((seg, i) => (
                            <option key={i} value={seg.id}>
                                {seg.title}
                            </option>
                        ))}
                    </select>

                    <button
                        onClick={handleExport}
                        className="group flex items-center justify-center gap-3 bg-sky-500/10 hover:bg-sky-500 border border-sky-500/20 px-6 py-3 rounded-2xl transition-all active:scale-95"
                    >
                        <span className="text-[10px] font-black uppercase tracking-widest text-sky-500 group-hover:text-black transition-colors">
                            {selectedSegmentId ? 'Download Segment CSV' : 'Download Event CSV'}
                        </span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-sky-500 group-hover:text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;